import { useMemo } from "react";
import { interpolateText, type TemplateContext } from "@tellescope/utils";
import type { Enduser } from "@tellescope/types-models";
import type { TellescopeModelFromName } from "@tellescope/tanstack-query";
import type { LocalTellescopeFormResponseMap } from "./use-tellescope-form-core";

export interface UseInterpolateFieldTextArgs {
  enduser: TellescopeModelFromName<"endusers"> | Enduser | undefined;
  currentResponses: LocalTellescopeFormResponseMap;
}

export interface InterpolateFieldText {
  /** For text React renders as-is, where React's own escaping applies. */
  text: (value: string | undefined) => string | undefined;
  /** For text spliced into HTML before sanitization — substituted values are entity-escaped. */
  html: (value: string | undefined) => string | undefined;
}

/** Renders authored text verbatim. For surfaces with no enduser context to substitute from. */
export const PASS_THROUGH_FIELD_TEXT: InterpolateFieldText = {
  text: (value) => value,
  html: (value) => value,
};

/**
 * Substitutes `{{enduser.*}}` template values into a field's authored title/description, so a form
 * can address the enduser by name or echo a value they already gave. Answers from the form in
 * progress take precedence over the enduser record, so computed tokens (`BMI`, `Age`) reflect what
 * was just entered rather than a stale record.
 *
 * `undefined` in, `undefined` out — callers pass optional field text straight through.
 */
export const useInterpolateFieldText = ({
  enduser,
  currentResponses,
}: UseInterpolateFieldTextArgs): InterpolateFieldText => {
  const context = useMemo(
    (): TemplateContext => ({
      enduser: (enduser as Enduser | undefined) ?? null,
      organization: null,
      snippets: {},
      responses: Object.values(currentResponses),
    }),
    [enduser, currentResponses]
  );

  return useMemo(
    () => ({
      text: (value: string | undefined) => (value === undefined ? undefined : interpolateText(value, context)),
      html: (value: string | undefined) =>
        value === undefined ? undefined : interpolateText(value, context, { escapeValues: true }),
    }),
    [context]
  );
};
